'use client';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import Link from 'next/link';
import { Home, Check, ChevronLeft, Palette } from 'lucide-react';
import { ALL_SKINS, SkinConfig, getSettings, saveSettings } from '../components/settingsStore';

const CATEGORIES = ['All', 'Futuristic', 'Neon', 'Clean', 'Gradient', 'Corrupted', 'Glitch', 'Plasma', 'Nuclear', 'Tech', 'Boss', 'Toxic'];

function SkinPreviewCanvas({ skin, size = 80, selected = false }: { skin: SkinConfig; size?: number; selected?: boolean }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animRef = useRef<number>(0);
  const startRef = useRef<number>(0);

  const draw = useCallback((ts: number) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d')!;
    if (!startRef.current) startRef.current = ts;
    const t = (ts - startRef.current) / 1000;
    const cx = size / 2, cy = size / 2;
    const r = size * 0.32;

    ctx.clearRect(0, 0, size, size);

    const pulse = 1 + Math.sin(t * 2.5) * 0.06;
    const pr = r * pulse;

    // Aura
    const aura = ctx.createRadialGradient(cx, cy, pr * 0.5, cx, cy, pr * 2.0);
    aura.addColorStop(0, skin.glowAura);
    aura.addColorStop(1, 'transparent');
    ctx.fillStyle = aura;
    ctx.beginPath(); ctx.arc(cx, cy, pr * 2.0, 0, Math.PI * 2); ctx.fill();

    // Glow
    ctx.shadowColor = skin.outerRingColor;
    ctx.shadowBlur = 10 + Math.sin(t * 3) * 4;

    // Body
    const grad = ctx.createRadialGradient(cx - pr * 0.3, cy - pr * 0.3, pr * 0.05, cx, cy, pr);
    grad.addColorStop(0, '#ffffff');
    grad.addColorStop(0.25, skin.bodyGradient[0]);
    grad.addColorStop(0.7, skin.bodyGradient[1]);
    grad.addColorStop(1, skin.bodyGradient[0] + '60');
    ctx.fillStyle = grad;
    ctx.beginPath(); ctx.arc(cx, cy, pr, 0, Math.PI * 2); ctx.fill();

    // Corruption cracks
    if (skin.corruptionCracks) {
      ctx.shadowBlur = 0;
      ctx.strokeStyle = 'rgba(0,0,0,0.5)';
      ctx.lineWidth = 1.2;
      for (let c = 0; c < 4; c++) {
        const angle = (c / 4) * Math.PI * 2 + t * 0.1;
        ctx.beginPath();
        ctx.moveTo(cx + Math.cos(angle) * pr * 0.3, cy + Math.sin(angle) * pr * 0.3);
        ctx.lineTo(cx + Math.cos(angle) * pr * 0.85, cy + Math.sin(angle) * pr * 0.85);
        ctx.stroke();
      }
    }

    // Tech markings
    if (skin.techMarkings) {
      ctx.shadowBlur = 0;
      ctx.strokeStyle = skin.outerRingColor + '60';
      ctx.lineWidth = 0.8;
      ctx.setLineDash([3, 3]);
      ctx.beginPath(); ctx.arc(cx, cy, pr * 0.65, 0, Math.PI * 2); ctx.stroke();
      ctx.setLineDash([]);
    }

    // Nucleus
    ctx.shadowBlur = 0;
    ctx.fillStyle = skin.nucleusColor;
    ctx.beginPath(); ctx.arc(cx, cy, pr * 0.28, 0, Math.PI * 2); ctx.fill();

    // Outer ring
    ctx.shadowColor = skin.outerRingColor;
    ctx.shadowBlur = 6;
    ctx.strokeStyle = skin.outerRingColor + 'cc';
    ctx.lineWidth = Math.max(1.5, pr * 0.07);
    const ringPulse = skin.ringPulse ? Math.sin(t * 4) * 0.08 : 0;
    ctx.beginPath(); ctx.arc(cx, cy, pr * (1.12 + ringPulse), 0, Math.PI * 2); ctx.stroke();

    // Second ring
    if (skin.secondRingColor) {
      ctx.strokeStyle = skin.secondRingColor + '88';
      ctx.lineWidth = Math.max(1, pr * 0.04);
      ctx.beginPath(); ctx.arc(cx, cy, pr * 1.28, 0, Math.PI * 2); ctx.stroke();
    }

    // Rotating segments for rotate/double effect
    if (skin.extraRingEffect === 'rotate' || skin.extraRingEffect === 'double') {
      ctx.strokeStyle = skin.outerRingColor + 'aa';
      ctx.lineWidth = Math.max(1.5, pr * 0.05);
      for (let seg = 0; seg < 4; seg++) {
        const segStart = (seg / 4) * Math.PI * 2 + t * 1.5;
        ctx.beginPath();
        ctx.arc(cx, cy, pr * 1.38, segStart, segStart + 0.35);
        ctx.stroke();
      }
    }

    ctx.shadowBlur = 0;
    animRef.current = requestAnimationFrame(draw);
  }, [skin, size]);

  useEffect(() => {
    animRef.current = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(animRef.current);
  }, [draw]);

  return (
    <canvas
      ref={canvasRef}
      width={size}
      height={size}
      style={{ display: 'block' }}
    />
  );
}

export default function CustomizationPage() {
  const [selectedSkinId, setSelectedSkinId] = useState('luma-prime');
  const [activeCategory, setActiveCategory] = useState('All');
  const [hoveredSkin, setHoveredSkin] = useState<string | null>(null);

  useEffect(() => {
    const settings = getSettings();
    setSelectedSkinId(settings.selectedSkinId || 'luma-prime');
  }, []);

  const handleSelectSkin = (skinId: string) => {
    setSelectedSkinId(skinId);
    saveSettings({ selectedSkinId: skinId });
  };

  const filteredSkins = activeCategory === 'All'
    ? ALL_SKINS
    : ALL_SKINS.filter(s => s.category === activeCategory);

  const selectedSkin = ALL_SKINS.find(s => s.id === selectedSkinId) || ALL_SKINS[0];

  return (
    <div
      className="min-h-screen text-white"
      style={{ background: 'linear-gradient(180deg, #020810 0%, #050a18 50%, #030610 100%)' }}
    >
      {/* Background glow */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full opacity-5" style={{ background: 'radial-gradient(circle, #bf00ff, transparent)', filter: 'blur(60px)' }} />
        <div className="absolute bottom-1/3 right-1/4 w-80 h-80 rounded-full opacity-5" style={{ background: 'radial-gradient(circle, #00f5ff, transparent)', filter: 'blur(60px)' }} />
      </div>

      {/* Nav */}
      <nav
        className="sticky top-0 z-50 flex items-center justify-between px-4 py-3"
        style={{
          background: 'rgba(2,8,16,0.9)',
          backdropFilter: 'blur(12px)',
          borderBottom: '1px solid rgba(191,0,255,0.1)',
        }}
      >
        <div className="flex items-center gap-3">
          <Link
            href="/"
            className="flex items-center gap-1.5 text-xs font-mono text-gray-400 hover:text-cyan-400 transition-colors px-2 py-1.5 rounded-lg min-h-[40px] min-w-[40px] justify-center"
            style={{ background: 'rgba(0,245,255,0.05)', border: '1px solid rgba(0,245,255,0.12)' }}
          >
            <Home size={14} />
            <span className="hidden sm:inline">Home</span>
          </Link>
          <Link
            href="/"
            className="flex items-center gap-1.5 text-xs font-mono text-gray-400 hover:text-cyan-400 transition-colors"
          >
            <ChevronLeft size={14} />
            <span className="hidden sm:inline">Back</span>
          </Link>
        </div>
        <div className="flex items-center gap-2">
          <Palette size={14} className="text-purple-400" />
          <span className="font-mono font-bold text-sm" style={{ color: '#bf00ff', textShadow: '0 0 10px rgba(191,0,255,0.5)' }}>
            LUMA CELL CUSTOMIZATION
          </span>
        </div>
        <div className="text-xs font-mono text-gray-600">
          {ALL_SKINS.length} skins
        </div>
      </nav>

      <div className="max-w-5xl mx-auto px-4 py-6 space-y-6">

        {/* Selected skin preview */}
        <div
          className="rounded-2xl p-6 flex flex-col sm:flex-row items-center gap-6"
          style={{
            background: 'rgba(5,10,25,0.8)',
            border: '1px solid rgba(191,0,255,0.2)',
            backdropFilter: 'blur(10px)',
          }}
        >
          <div
            className="rounded-full overflow-hidden shrink-0"
            style={{
              background: 'rgba(5,10,25,0.6)',
              border: `2px solid ${selectedSkin.outerRingColor}60`,
              boxShadow: `0 0 30px ${selectedSkin.outerRingColor}40`,
            }}
          >
            <SkinPreviewCanvas skin={selectedSkin} size={120} selected />
          </div>
          <div className="flex-1 text-center sm:text-left">
            <div className="text-xs font-mono text-gray-500 uppercase tracking-widest mb-1">{selectedSkin.category}</div>
            <h2 className="text-2xl font-black font-mono mb-2" style={{ color: selectedSkin.outerRingColor }}>
              {selectedSkin.name}
            </h2>
            <div className="flex flex-wrap gap-2 justify-center sm:justify-start">
              {selectedSkin.ringPulse && (
                <span className="text-[10px] font-mono px-2 py-0.5 rounded" style={{ background: 'rgba(0,245,255,0.1)', color: '#00f5ff', border: '1px solid rgba(0,245,255,0.2)' }}>Ring Pulse</span>
              )}
              {selectedSkin.techMarkings && (
                <span className="text-[10px] font-mono px-2 py-0.5 rounded" style={{ background: 'rgba(0,102,255,0.1)', color: '#0066ff', border: '1px solid rgba(0,102,255,0.2)' }}>Tech Markings</span>
              )}
              {selectedSkin.corruptionCracks && (
                <span className="text-[10px] font-mono px-2 py-0.5 rounded" style={{ background: 'rgba(191,0,255,0.1)', color: '#bf00ff', border: '1px solid rgba(191,0,255,0.2)' }}>Corruption Cracks</span>
              )}
              {selectedSkin.secondRingColor && (
                <span className="text-[10px] font-mono px-2 py-0.5 rounded" style={{ background: 'rgba(255,102,0,0.1)', color: '#ff6600', border: '1px solid rgba(255,102,0,0.2)' }}>Dual Ring</span>
              )}
              {selectedSkin.extraRingEffect && selectedSkin.extraRingEffect !== 'none' && (
                <span className="text-[10px] font-mono px-2 py-0.5 rounded capitalize" style={{ background: 'rgba(57,255,20,0.1)', color: '#39ff14', border: '1px solid rgba(57,255,20,0.2)' }}>{selectedSkin.extraRingEffect} Effect</span>
              )}
            </div>
            <p className="text-xs text-gray-500 font-mono mt-3">
              Nucleus: <span style={{ color: selectedSkin.nucleusColor }}>■</span> &nbsp;
              Ring: <span style={{ color: selectedSkin.outerRingColor }}>■</span>
              {selectedSkin.secondRingColor && <> &nbsp; 2nd Ring: <span style={{ color: selectedSkin.secondRingColor }}>■</span></>}
            </p>
          </div>
          <div className="shrink-0">
            <div
              className="px-4 py-2 rounded-lg text-xs font-mono font-bold"
              style={{
                background: 'rgba(0,245,255,0.1)',
                border: '1px solid rgba(0,245,255,0.3)',
                color: '#00f5ff',
              }}
            >
              ✓ Active Skin
            </div>
          </div>
        </div>

        {/* Category filter */}
        <div className="flex flex-wrap gap-2">
          {CATEGORIES.map(cat => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className="px-3 py-1.5 rounded-lg text-xs font-mono font-bold transition-all duration-150 min-h-[36px]"
              style={{
                background: activeCategory === cat ? 'rgba(191,0,255,0.15)' : 'rgba(255,255,255,0.04)',
                border: activeCategory === cat ? '1px solid rgba(191,0,255,0.4)' : '1px solid rgba(255,255,255,0.08)',
                color: activeCategory === cat ? '#bf00ff' : 'rgba(150,150,170,0.7)',
              }}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Skin grid */}
        <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-10 gap-2">
          {filteredSkins.map(skin => {
            const isSelected = skin.id === selectedSkinId;
            const isHovered = hoveredSkin === skin.id;
            return (
              <div
                key={skin.id}
                className="relative flex flex-col items-center gap-1 cursor-pointer transition-transform duration-150"
                style={{ transform: isHovered ? 'scale(1.1)' : 'scale(1)' }}
                onMouseEnter={() => setHoveredSkin(skin.id)}
                onMouseLeave={() => setHoveredSkin(null)}
                onClick={() => handleSelectSkin(skin.id)}
              >
                <div
                  className="rounded-full overflow-hidden relative"
                  style={{
                    background: 'rgba(5,10,25,0.6)',
                    border: isSelected
                      ? `2px solid ${skin.outerRingColor}`
                      : `1px solid ${skin.outerRingColor}30`,
                    boxShadow: isSelected
                      ? `0 0 16px ${skin.outerRingColor}60`
                      : isHovered ? `0 0 10px ${skin.outerRingColor}40` : 'none',
                    transition: 'box-shadow 0.15s, border 0.15s',
                  }}
                >
                  <SkinPreviewCanvas skin={skin} size={64} selected={isSelected} />
                  {isSelected && (
                    <div
                      className="absolute inset-0 flex items-center justify-center rounded-full"
                      style={{ background: 'rgba(0,0,0,0.4)' }}
                    >
                      <Check size={20} className="text-white" />
                    </div>
                  )}
                </div>
                <span
                  className="text-[8px] font-mono text-center leading-tight truncate w-full text-center px-0.5"
                  style={{ color: isSelected ? skin.outerRingColor : 'rgba(150,150,170,0.6)' }}
                >
                  {skin.name}
                </span>
              </div>
            );
          })}
        </div>

        {/* Info */}
        <div
          className="rounded-xl p-4 text-center"
          style={{
            background: 'rgba(5,10,25,0.6)',
            border: '1px solid rgba(0,245,255,0.08)',
          }}
        >
          <p className="text-xs font-mono text-gray-500">
            Skin is saved automatically · Applied in-game and on home screen · {ALL_SKINS.length} unique skins available
          </p>
          <div className="mt-3 flex justify-center gap-3">
            <Link
              href="/"
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-mono font-bold transition-all duration-200 hover:scale-105 min-h-[44px]"
              style={{
                background: 'rgba(0,245,255,0.1)',
                border: '1px solid rgba(0,245,255,0.3)',
                color: '#00f5ff',
              }}
            >
              <Home size={14} />
              Return Home
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
